import { deriveTitle, extractTags } from './markdown.ts';
import type { LocalNote } from './types.ts';

/**
 * Verknüpfungen zwischen Notizen (docs/KONZEPT.md, Abschnitt 9).
 *
 * `[[Wikilinks]]` zeigen auf Titel, nicht auf IDs — genau wie in Obsidian. Das hält
 * den Markdown-Text lesbar und portabel; die Auflösung passiert hier.
 *
 * Der Editor lebt außerhalb von React, deshalb hält dieses Modul eine schlanke
 * Registry, die der Store bei jeder Änderung nachführt.
 */

let notes: LocalNote[] = [];
let byTitle = new Map<string, string>();
let navigator_: ((target: string) => void) | null = null;
let searcher: ((query: string) => void) | null = null;

export function setNotes(next: LocalNote[]): void {
  notes = next;
  const index = new Map<string, string>();
  for (const note of next) {
    if (note.deletedAt !== null) continue;
    const title = deriveTitle(note.content).toLowerCase();
    // Bei doppelten Titeln gewinnt die zuletzt bearbeitete Notiz.
    const existing = index.get(title);
    if (!existing || (notes.find((n) => n.id === existing)?.updatedAt ?? 0) < note.updatedAt) {
      index.set(title, note.id);
    }
  }
  byTitle = index;
}

export function setNavigator(handler: (target: string) => void): void {
  navigator_ = handler;
}

export function setSearcher(handler: (query: string) => void): void {
  searcher = handler;
}

/** Teil vor dem senkrechten Strich ist das Ziel, dahinter steht nur der Anzeigetext. */
export function linkTarget(inner: string): string {
  return (inner.split('|')[0] ?? inner).trim();
}

export function resolve(target: string): string | null {
  return byTitle.get(linkTarget(target).toLowerCase()) ?? null;
}

export function followLink(target: string): void {
  navigator_?.(target);
}

export function searchFor(query: string): void {
  searcher?.(query);
}

export function titles(): string[] {
  return notes
    .filter((note) => note.deletedAt === null)
    .sort((a, b) => b.updatedAt - a.updatedAt)
    .map((note) => deriveTitle(note.content))
    .filter((title) => title !== 'Ohne Titel');
}

export function allTags(): string[] {
  const counts = new Map<string, number>();
  for (const note of notes) {
    if (note.deletedAt !== null) continue;
    for (const tag of extractTags(note.content)) counts.set(tag, (counts.get(tag) ?? 0) + 1);
  }
  return [...counts.entries()].sort((a, b) => b[1] - a[1]).map(([tag]) => tag);
}

const WIKILINK = /\[\[([^\]\n]+)\]\]/g;

export function outgoingLinks(content: string): string[] {
  return [...content.matchAll(WIKILINK)].map((match) => linkTarget(match[1] ?? ''));
}

/** Notizen, die auf diese hier zeigen — die Umkehrung der Wikilinks. */
export function backlinksOf(noteId: string): LocalNote[] {
  const note = notes.find((entry) => entry.id === noteId);
  if (!note) return [];
  const title = deriveTitle(note.content).toLowerCase();
  if (title === 'ohne titel') return [];
  return notes.filter(
    (candidate) =>
      candidate.id !== noteId &&
      candidate.deletedAt === null &&
      outgoingLinks(candidate.content).some((target) => target.toLowerCase() === title),
  );
}
